import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
} from "../components";
import { menuService } from "../services";

function SubMenuPage() {
  const { parentId, menuSlug } = useParams();
  const navigate = useNavigate();
  const [parentMenu, setParentMenu] = useState(null);
  const [submenus, setSubmenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch parent menu and submenus
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch parent menu info
        const parentResponse = await menuService.getMenuById(parentId);
        const parentData = parentResponse.data || parentResponse;
        setParentMenu(parentData);

        // Fetch submenus
        const submenuResponse = await menuService.getSubmenuByParentId(parentId);
        const submenuData = submenuResponse.data || submenuResponse.menus || submenuResponse;
        
        if (Array.isArray(submenuData)) {
          setSubmenus(submenuData);
        } else {
          setSubmenus([]);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching submenu data:', err);
        setError('Gagal memuat data submenu. Silakan coba lagi nanti.');
        setSubmenus([]);
      } finally {
        setLoading(false);
      }
    };

    if (parentId) {
      fetchData();
    }
  }, [parentId]);

  // Get logo path helper
  const getLogoPath = (logoFilename) => {
    try {
      return new URL(`../assets/images/logo/${logoFilename}`, import.meta.url).href;
    } catch (error) {
      return new URL(`../assets/images/logo/logo.png`, import.meta.url).href;
    }
  };

  // Handle back button
  const handleBack = () => {
    navigate('/');
  };

  // Handle submenu click
  const handleSubmenuClick = (submenu) => {
    if (submenu.url) {
      window.open(submenu.url, submenu.target || '_blank');
    }
  };

  return (
    <>
      <div className="page-heading m-2">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h3 className="mb-2">
              {parentMenu?.nama_aplikasi || 'Submenu'}
            </h3>
            {parentMenu?.deskripsi && (
              <p className="text-subtitle text-muted mb-0">{parentMenu.deskripsi}</p>
            )}
          </div>
          <button 
            className="btn btn-outline-primary"
            onClick={handleBack}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Kembali
          </button>
        </div>

        {/* Breadcrumb */}
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/" onClick={(e) => { e.preventDefault(); handleBack(); }}>
                <i className="bi bi-house-door me-1"></i>
                Beranda
              </a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {parentMenu?.nama_aplikasi}
            </li>
          </ol>
        </nav>
      </div>

      <div className="page-content m-2">
        <section className="row">
          {loading ? (
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Memuat data submenu...</p>
            </div>
          ) : error ? (
            <div className="col-12">
              <div className="alert alert-warning alert-dismissible fade show" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setError(null)}
                  aria-label="Close"
                ></button>
              </div>
            </div>
          ) : submenus.length === 0 ? (
            <div className="col-12">
              <Card>
                <CardBody className="text-center py-5">
                  <i className="bi bi-inbox fs-1 text-muted d-block mb-3"></i>
                  <h5 className="text-muted">Tidak ada submenu</h5>
                  <p className="text-muted mb-4">
                    Belum ada submenu yang tersedia untuk kategori ini.
                  </p>
                  <button 
                    className="btn btn-primary"
                    onClick={handleBack}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Kembali ke Beranda
                  </button>
                </CardBody>
              </Card>
            </div>
          ) : (
            submenus.map((submenu) => (
              <div key={submenu.id} className="col-12 col-md-6 col-lg-4 d-flex">
                <Card className="w-100 submenu-card mb-3 shadow-sm">
                  <CardBody>
                    <div 
                      className="d-flex align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSubmenuClick(submenu)}
                    >
                      <div className="flex-shrink-0 me-3">
                        {submenu.logo ? (
                          <img 
                            src={getLogoPath(submenu.logo)} 
                            alt={`Logo ${submenu.nama_aplikasi}`}
                            className="rounded"
                            style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                            onError={(e) => {
                              e.target.src = getLogoPath('logo.png');
                            }}
                          />
                        ) : (
                          <div 
                            className="d-flex align-items-center justify-content-center rounded bg-light text-muted"
                            style={{ width: '60px', height: '60px', fontSize: '2rem' }}
                          >
                            <i className="bi bi-app"></i>
                          </div>
                        )}
                      </div>
                      <div className="flex-grow-1">
                        <h5 className="mb-1 d-flex align-items-center justify-content-between">
                          <span>{submenu.nama_aplikasi}</span>
                          <i className="bi bi-arrow-right-circle text-muted"></i>
                        </h5>
                        <p className="text-muted mb-0 small">{submenu.deskripsi}</p>
                        {submenu.kategori && (
                          <span className={`badge bg-${submenu.warna || 'primary'} mt-2`}>
                            {submenu.kategori}
                          </span>
                        )}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            ))
          )}
        </section>

        {/* Info Section */}
        {!loading && !error && submenus.length > 0 && (
          <section className="row mt-4">
            <div className="col-12">
              <div className="alert alert-info d-flex align-items-center" role="alert">
                <i className="bi bi-info-circle-fill me-3 fs-4"></i>
                <div>
                  <strong>Informasi:</strong> Klik pada card untuk membuka aplikasi di tab baru.
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}

export default SubMenuPage;
